import { z } from 'zod';
import type { LoginResponse } from './auth.js';
import { novaSenhaSchema } from './auth.js';
import { PAPEIS, PAPEIS_DE_EMPRESA, type AcessoDto, type Papel } from './permissoes.js';
import { cuid } from './primitives.js';

// Usuarios e acessos (Sessao 5, E6; docs/PERMISSOES.md secao 4). A regra do TETO mora aqui, pura, para a API (quem nega)
// e o web (so esconde opcoes) usarem a mesma: ninguem concede papel acima do seu nem fora do seu escopo.

/** Posto do papel no teto (menor = mais alto). Os papeis de empresa nao tem ordem entre si, todos abaixo de ADMIN_EMPRESA. */
export const POSTO_DO_PAPEL: Readonly<Record<Papel, number>> = {
  SUPERADMIN: 0,
  ADMIN_TENANT: 1,
  CONSULTOR: 2,
  ADMIN_EMPRESA: 3,
  FINANCEIRO: 4,
  ORCAMENTISTA: 4,
  VENDEDOR: 4,
  REPRESENTANTE: 4,
  PCP: 4,
  RH: 4,
  LEITURA: 4,
};

/** Papeis administrativos: quem perde o ultimo deles perde a gestao de usuarios (409 ULTIMO_ADMIN). */
export const PAPEIS_ADMINISTRATIVOS: readonly Papel[] = ['SUPERADMIN', 'ADMIN_TENANT', 'CONSULTOR', 'ADMIN_EMPRESA'];

/** Empresa como o teto a enxerga: id e grupo (o acesso por grupo alcanca as empresas dele). */
export interface EmpresaRef {
  id: string;
  grupoId: string;
}

/** O acesso alcanca a empresa? Tenant inteiro (sem empresa nem grupo), o grupo dela ou ela mesma. */
export function acessoAlcancaEmpresa(a: AcessoDto, empresa: EmpresaRef): boolean {
  return (a.empresaId === null && a.grupoId === null) || a.empresaId === empresa.id || (a.grupoId !== null && a.grupoId === empresa.grupoId);
}

/** Posto de quem tem estes acessos naquela empresa: o menor entre os papeis que a alcancam (Infinity = nao alcanca). */
export function postoNaEmpresa(acessos: readonly AcessoDto[], empresa: EmpresaRef): number {
  let posto = Number.POSITIVE_INFINITY;
  for (const a of acessos) if (acessoAlcancaEmpresa(a, empresa)) posto = Math.min(posto, POSTO_DO_PAPEL[a.papel]);
  return posto;
}

/** Posto no tenant: so os acessos de tenant inteiro (SUPERADMIN, ADMIN_TENANT) contam. */
export function postoNoTenant(acessos: readonly AcessoDto[]): number {
  let posto = Number.POSITIVE_INFINITY;
  for (const a of acessos) if (a.empresaId === null && a.grupoId === null) posto = Math.min(posto, POSTO_DO_PAPEL[a.papel]);
  return posto;
}

/** Papeis que se concede (ou remove) numa empresa. SA/AT: CONSULTOR, ADMIN_EMPRESA e os de empresa; CONSULTOR: ADMIN_EMPRESA e os de empresa; ADMIN_EMPRESA: so os de empresa abaixo dele. */
export function papeisConcediveisNaEmpresa(acessos: readonly AcessoDto[], empresa: EmpresaRef): Papel[] {
  const posto = postoNaEmpresa(acessos, empresa);
  if (posto <= POSTO_DO_PAPEL.ADMIN_TENANT) return ['CONSULTOR', ...PAPEIS_DE_EMPRESA];
  if (posto === POSTO_DO_PAPEL.CONSULTOR) return [...PAPEIS_DE_EMPRESA];
  if (posto === POSTO_DO_PAPEL.ADMIN_EMPRESA) return PAPEIS_DE_EMPRESA.filter((p) => p !== 'ADMIN_EMPRESA');
  return [];
}

/** Papeis de tenant inteiro: SUPERADMIN concede SUPERADMIN e ADMIN_TENANT; ADMIN_TENANT concede ADMIN_TENANT. */
export function papeisConcediveisNoTenant(acessos: readonly AcessoDto[]): Papel[] {
  const posto = postoNoTenant(acessos);
  if (posto === POSTO_DO_PAPEL.SUPERADMIN) return ['SUPERADMIN', 'ADMIN_TENANT'];
  if (posto === POSTO_DO_PAPEL.ADMIN_TENANT) return ['ADMIN_TENANT'];
  return [];
}

/** CONSULTOR por grupo inteiro: so quem tem tenant inteiro (SA, AT) concede. */
export function papeisConcediveisNoGrupo(acessos: readonly AcessoDto[]): Papel[] {
  return postoNoTenant(acessos) <= POSTO_DO_PAPEL.ADMIN_TENANT ? ['CONSULTOR'] : [];
}

/**
 * Quem tem `acessos` pode conceder (ou remover) `alvo`? `empresas` sao as empresas do tenant que o concedente enxerga:
 * empresa fora dessa lista = fora do escopo (CONSULTOR nao sai das empresas dele). Papel de empresa exige empresa.
 */
export function podeConceder(acessos: readonly AcessoDto[], empresas: readonly EmpresaRef[], alvo: AcessoDto): boolean {
  if (alvo.papel === 'SUPERADMIN' || alvo.papel === 'ADMIN_TENANT') {
    return alvo.empresaId === null && alvo.grupoId === null && papeisConcediveisNoTenant(acessos).includes(alvo.papel);
  }
  if (alvo.papel === 'CONSULTOR' && alvo.empresaId === null) {
    return alvo.grupoId !== null && empresas.some((e) => e.grupoId === alvo.grupoId) && papeisConcediveisNoGrupo(acessos).includes('CONSULTOR');
  }
  if (alvo.empresaId === null || alvo.grupoId !== null) return false;
  const empresa = empresas.find((e) => e.id === alvo.empresaId);
  if (!empresa) return false;
  return papeisConcediveisNaEmpresa(acessos, empresa).includes(alvo.papel);
}

/** Papeis do usuario que o teto de `acessos` nao deixa mexer? Util para decidir se desativar (todas as empresas) e permitido. */
export function todosConcediveis(acessos: readonly AcessoDto[], empresas: readonly EmpresaRef[], alvos: readonly AcessoDto[]): boolean {
  return alvos.every((a) => podeConceder(acessos, empresas, a));
}

// ---------------------------------------------------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------------------------------------------------

const papelEnum = z.enum(PAPEIS);

/** Acesso pedido: mesmo CHECK do banco (SUPERADMIN/ADMIN_TENANT sem empresa nem grupo; CONSULTOR um dos dois; demais, empresa). */
export const acessoSchema = z
  .object({
    papel: papelEnum,
    empresaId: cuid.nullish(),
    grupoId: cuid.nullish(),
  })
  .superRefine((a, ctx) => {
    const emp = a.empresaId ?? null;
    const grp = a.grupoId ?? null;
    const erro = (message: string) => ctx.addIssue({ code: 'custom', path: ['papel'], message });
    if (a.papel === 'SUPERADMIN' || a.papel === 'ADMIN_TENANT') {
      if (emp !== null || grp !== null) erro('Papel de tenant inteiro nao leva empresa nem grupo');
    } else if (a.papel === 'CONSULTOR') {
      if ((emp === null) === (grp === null)) erro('Consultor vale em uma empresa ou em um grupo (so um dos dois)');
    } else if (emp === null || grp !== null) {
      erro('Este papel vale em uma empresa');
    }
  });
export type AcessoDef = z.infer<typeof acessoSchema>;

const emailConvite = z.email('E-mail invalido').max(254, 'E-mail muito longo');
const nomeConvite = z.string().trim().min(1, 'Informe o nome').max(120, 'O nome tem no maximo 120 caracteres');

/** POST /usuarios/convites. O e-mail e guardado em minusculas pela API. */
export const convidarSchema = z.object({
  email: emailConvite,
  nome: nomeConvite.optional(),
  acessos: z.array(acessoSchema).min(1, 'Escolha ao menos um acesso').max(30),
});
export type ConvidarDto = z.infer<typeof convidarSchema>;

/** Formulario do dialogo de convite: nome opcional em branco, acessos escolhidos pela grade de empresas x papeis. */
export const convidarFormSchema = z.object({
  email: emailConvite,
  nome: z.string().trim().max(120, 'O nome tem no maximo 120 caracteres'),
  acessos: z.array(acessoSchema).min(1, 'Escolha ao menos um acesso'),
});
export type ConvidarFormValores = z.infer<typeof convidarFormSchema>;

/** POST /usuarios/:id/acessos. */
export const novoAcessoSchema = acessoSchema;
export type NovoAcessoDto = AcessoDef;

/** POST /convites/aceitar (publico): quem aceita define a propria senha. */
export const aceitarConviteSchema = z.object({
  token: z.string().min(20, 'Convite invalido').max(200, 'Convite invalido'),
  nome: nomeConvite.optional(),
  senha: novaSenhaSchema,
});
export type AceitarConviteDto = z.infer<typeof aceitarConviteSchema>;

/** Tela de aceite: confirmacao so no front. */
export const aceitarConviteFormSchema = z
  .object({
    nome: z.string().trim().max(120, 'O nome tem no maximo 120 caracteres'),
    senha: novaSenhaSchema,
    confirmacao: z.string().min(1, 'Repita a senha'),
  })
  .refine((v) => v.senha === v.confirmacao, { path: ['confirmacao'], message: 'A confirmação não confere com a senha' });
export type AceitarConviteFormValores = z.infer<typeof aceitarConviteFormSchema>;

// ---------------------------------------------------------------------------------------------------------------
// Saida
// ---------------------------------------------------------------------------------------------------------------

export interface AcessoLinha {
  id: string;
  papel: Papel;
  empresaId: string | null;
  empresaNome: string | null;
  grupoId: string | null;
  grupoNome: string | null;
  /** Perfil proprio da empresa (docs/PERMISSOES.md secao 11); ausente/nulo = perfil padrao do papel. */
  perfilNome?: string | null;
  /** O teto de quem consulta permite remover este acesso. */
  podeRemover: boolean;
}

export interface UsuarioLinha {
  id: string;
  nome: string;
  email: string;
  ativo: boolean;
  /** Ainda nao trocou a senha temporaria. */
  deveTrocarSenha: boolean;
  createdAt: string;
  acessos: AcessoLinha[];
  /** Acessos dele fora do escopo de quem consulta (nao aparecem; contam so para explicar o "Desativar" negado). */
  acessosForaDoEscopo: number;
  ehVoce: boolean;
  podeDesativar: boolean;
  podeReativar: boolean;
}

export type SituacaoConvite = 'PENDENTE' | 'ACEITO' | 'REVOGADO' | 'EXPIRADO';

export interface ConviteAcessoLinha {
  papel: Papel;
  empresaId: string | null;
  empresaNome: string | null;
  grupoId: string | null;
  grupoNome: string | null;
}

export type EmailConviteStatus = 'ENVIADO' | 'FALHOU' | 'NAO_CONFIGURADO';

export interface ConviteLinha {
  id: string;
  email: string;
  nome: string | null;
  acessos: ConviteAcessoLinha[];
  expiraEm: string;
  situacao: SituacaoConvite;
  createdAt: string;
  criadoPorNome: string | null;
  podeRevogar: boolean;
  /** Sessao 48: rastro do e-mail. Nulo = convite anterior ao rastro. */
  emailStatus: EmailConviteStatus | null;
  emailEnviadoEm: string | null;
  /** Descricao tecnica da falha (sem credenciais, ate 300 caracteres); so quando FALHOU. */
  emailErro: string | null;
  /** Motivo da falha em linguagem simples; so quando FALHOU. */
  emailMotivo: string | null;
}

/** O que quem consulta pode conceder, por onde: alimenta o seletor de acessos do dialogo (o web so esconde; a API nega). */
export interface OpcoesConcessao {
  tenant: Papel[];
  grupos: Array<{ id: string; nome: string; papeis: Papel[] }>;
  empresas: Array<{ id: string; nome: string; grupoId: string; papeis: Papel[] }>;
}

export interface UsuariosResponse {
  usuarios: UsuarioLinha[];
  convites: ConviteLinha[];
  opcoes: OpcoesConcessao;
}

/** Resposta de criar/reenviar convite. Sem SMTP (ou se o envio falhar) `link` traz o endereco para copiar: so aparece aqui, uma vez. */
export interface ConviteCriado {
  convite: ConviteLinha;
  emailEnviado: boolean;
  envioFalhou: boolean;
  link: string | null;
}

/** GET /convites/:token (publico): o suficiente para a tela de aceite. */
export interface ConvitePrevia {
  email: string;
  nome: string | null;
  organizacao: string;
  acessos: ConviteAcessoLinha[];
  expiraEm: string;
}

export type AceitarConviteResponse = LoginResponse;
